import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';

import {
    ABOUT_US_LINK,
    CONTACT_US_LINK,
    FACEBOOK_LINK,
    FEATURES_LINK,
    FOR_SCHOOLS_LINK,
    FOR_TUTORING_LINK,
    SPECIAL_QUOTA_LINK,
    SUBSCRIPTIONS_LINK,
    TUTORIAL_LINK,
    YOUTUBE_LINK,
} from '../../common/urlLinks';
import { REDESIGN_COLORS, REDESIGN_FONTS } from '../../constants';

// The design points these at in-page anchors (#preturi, #contexte, #scoli).
// This footer renders on every route, so they resolve to the real routes
// instead - an anchor would go nowhere from anywhere but Home.
const GROUPS = [
    {
        heading: 'group.resources',
        links: [
            { key: 'link.howItWorks', to: FEATURES_LINK },
            { key: 'link.tutorials', to: TUTORIAL_LINK },
            { key: 'link.pricing', to: SUBSCRIPTIONS_LINK },
            { key: 'link.login', login: true },
        ],
    },
    {
        heading: 'group.audience',
        links: [
            { key: 'link.teachers', to: FEATURES_LINK },
            { key: 'link.tutors', to: FOR_TUTORING_LINK },
            { key: 'link.schools', to: FOR_SCHOOLS_LINK },
        ],
    },
    {
        heading: 'group.company',
        links: [
            { key: 'link.about', to: ABOUT_US_LINK },
            { key: 'link.partners', to: `${ABOUT_US_LINK}/partners` },
            { key: 'link.media', to: `${ABOUT_US_LINK}/media` },
            { key: 'link.contact', to: CONTACT_US_LINK },
        ],
    },
    {
        heading: 'group.legal',
        links: [
            { key: 'link.terms', to: '/terms' },
            { key: 'link.privacy', to: '/privacypolicy' },
            { key: 'link.specialQuota', to: SPECIAL_QUOTA_LINK },
        ],
    },
];

// The design has no media queries: its auto-fit grid collapses to one column
// on phones, stacking the four link groups into one long list. Below this
// width the groups sit two by two under the brand block instead.
const PHONE = '@media (max-width: 760px)';

const useStyles = makeStyles(() => ({
    footer: {
        // Both shells lay the page out as a stretched flex column, so this pins
        // the footer to the bottom of the viewport when the content is short.
        marginTop: 'auto',
        padding: '0 0 45px',
        background: REDESIGN_COLORS.cream,
        fontFamily: REDESIGN_FONTS.body,
    },
    inner: {
        boxSizing: 'border-box',
        width: 'min(1160px, calc(100% - 40px))',
        margin: '0 auto',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))',
        gap: '34px',
        paddingTop: '38px',
        borderTop: `1px solid ${REDESIGN_COLORS.line}`,
        [PHONE]: {
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: '28px 20px',
            paddingTop: '30px',
        },
    },
    brand: {
        [PHONE]: {
            gridColumn: '1 / -1',
        },
        color: REDESIGN_COLORS.body,
        fontSize: '0.84rem',
        lineHeight: 1.65,
    },
    logo: {
        display: 'block',
        width: '128px',
        height: 'auto',
        marginBottom: '14px',
    },
    row: {
        marginTop: '8px',
    },
    strong: {
        color: REDESIGN_COLORS.dark,
    },
    group: {
        display: 'grid',
        alignContent: 'start',
        gap: '9px',
    },
    groupHeading: {
        marginBottom: '4px',
        color: REDESIGN_COLORS.dark,
        fontSize: '0.84rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
    },
    link: {
        color: REDESIGN_COLORS.body,
        fontSize: '0.8rem',
        textAlign: 'left',
        '&:hover': {
            color: REDESIGN_COLORS.link,
        },
    },
    // The login entry opens the modal rather than navigating, so it is a button
    // styled to sit in the same list.
    linkButton: {
        justifySelf: 'start',
        padding: 0,
        border: 0,
        background: 'none',
        fontFamily: 'inherit',
        cursor: 'pointer',
    },
    social: {
        display: 'flex',
        gap: '14px',
        margin: '12px 0 0',
        padding: 0,
        listStyle: 'none',
    },
    socialLink: {
        display: 'inline-flex',
        color: REDESIGN_COLORS.body,
        '&:hover': {
            color: REDESIGN_COLORS.link,
        },
    },
}));

/**
 * The redesigned footer, rendered on every route, signed out and signed in.
 * Signed in, the login entry is dropped (the side menu carries logout).
 *
 * The design drops the social links the current footer carries; they are kept
 * here so nothing is lost in the move.
 */
const RedesignFooter = ({ handleOpen }) => {
    const classes = useStyles();
    const intl = useIntl();
    const loggedIn = useSelector((state) => state.profile.loggedIn);

    return (
        <footer className={classes.footer}>
            <div className={classes.inner}>
                <div className={classes.brand}>
                    <img src="/VBlogo.svg" alt="Virtual Board" className={classes.logo} />
                    <div>
                        <FormattedMessage id="redesign.footer.tagline" />
                    </div>
                    <div className={classes.row}>
                        <strong className={classes.strong}>
                            <FormattedMessage id="redesign.footer.contactLabel" />
                        </strong>{' '}
                        <Link to={CONTACT_US_LINK} className={classes.link}>
                            <FormattedMessage id="redesign.footer.contactLink" />
                        </Link>
                    </div>
                    <ul className={classes.social}>
                        <li>
                            <a
                                href={FACEBOOK_LINK}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={classes.socialLink}
                                aria-label={intl.formatMessage({ id: 'redesign.footer.social.facebook' })}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                    <path d="M14 9h3V6h-3c-2.2 0-4 1.8-4 4v2H8v3h2v6h3v-6h2.5l.5-3H13v-2c0-.6.4-1 1-1z" />
                                </svg>
                            </a>
                        </li>
                        <li>
                            <a
                                href={YOUTUBE_LINK}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={classes.socialLink}
                                aria-label={intl.formatMessage({ id: 'redesign.footer.social.youtube' })}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                    <path d="M21.6 7.2a2.5 2.5 0 0 0-1.8-1.8C18.2 5 12 5 12 5s-6.2 0-7.8.4A2.5 2.5 0 0 0 2.4 7.2C2 8.8 2 12 2 12s0 3.2.4 4.8a2.5 2.5 0 0 0 1.8 1.8C5.8 19 12 19 12 19s6.2 0 7.8-.4a2.5 2.5 0 0 0 1.8-1.8C22 15.2 22 12 22 12s0-3.2-.4-4.8zM10 15.5v-7l6 3.5-6 3.5z" />
                                </svg>
                            </a>
                        </li>
                    </ul>
                    <div className={classes.row}>
                        <FormattedMessage
                            id="redesign.footer.copyright"
                            values={{ year: new Date().getFullYear() }}
                        />
                    </div>
                </div>

                {GROUPS.map((group) => (
                    <div key={group.heading} className={classes.group}>
                        <strong className={classes.groupHeading}>
                            <FormattedMessage id={`redesign.footer.${group.heading}`} />
                        </strong>
                        {group.links
                            .filter((link) => !(link.login && loggedIn))
                            .map((link) =>
                            link.login ? (
                                <button
                                    key={link.key}
                                    type="button"
                                    onClick={() => handleOpen && handleOpen()}
                                    className={`${classes.link} ${classes.linkButton}`}
                                >
                                    <FormattedMessage id={`redesign.footer.${link.key}`} />
                                </button>
                            ) : (
                                <Link key={link.key} to={link.to} className={classes.link}>
                                    <FormattedMessage id={`redesign.footer.${link.key}`} />
                                </Link>
                            )
                        )}
                    </div>
                ))}
            </div>
        </footer>
    );
};

export default RedesignFooter;
